import { FormikProps, FormikProvider } from 'formik';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import DateRangeInput from '@/components/common/Inputs/DateRangeInput/DateRangeInput';
import ComponentLoader from '@/components/common/Loaders/ComponentLoader';
import { FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import FQTextField, { FQTEXT_FIELD_VARIANTS } from '@/components/muiCustomized/FQTextField/FQTextField';
import { RequestNoRestrictionSummary, RequestWithRestrictionSummary } from '@/domain/newRequest';
import { MEDIA_QUERIES_BREAKPOINTS, PX_TO_REM } from '@/globalStyles';
import { LimitsUsage } from '@/hooks/useUsedLimits/useUsedLimits';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import Calendar from '@/pages/NewRequest/Calendar/Calendar';
import RequestTypeCards from '@/pages/NewRequest/RequestTypeCards/RequestTypeCards';
import Summary from '@/pages/NewRequest/Summary/Summary';
import { NEW_REQUEST_FIELD, NewRequestFields } from '@/pages/NewRequest/useNewRequestLogic';
import UsersSelect from '@/pages/NewRequest/UsersSelect/UsersSelect';
import ApproversBox from '@/pages/Profile/common/ApproversBox/ApproversBox';
import { DEFAULT_PROTECTED_ROUTE } from '@/pages/Routing/Routing';

import SuccessModal from '../UsersSelect/components/SuccessModal';

interface NewRequestFormProps {
    formik: FormikProps<NewRequestFields>;
    limitsUsage?: LimitsUsage[];
    requestSummary?: RequestNoRestrictionSummary & RequestWithRestrictionSummary;
    isLimitExceeded: boolean;
    isLoadingSummary: boolean;
    isSubmitting: boolean;
    isCreatedSuccess: boolean;

    reloadDataOnReset: unknown[];
    isLead: boolean;
    showButtons?: boolean;
    isScheduleView?: boolean;
    onReset: () => void;
    onCancel?: () => void;
}

const NewRequestForm = ({
    formik,
    limitsUsage,
    requestSummary,
    isLimitExceeded,
    isLoadingSummary,
    isSubmitting,
    isCreatedSuccess,
    onReset,
    reloadDataOnReset,
    isLead,
    showButtons = true,
    isScheduleView,
    onCancel,
}: NewRequestFormProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { values, touched, errors, handleChange, setFieldValue, handleSubmit, handleBlur } = formik;

    const endDateNotFilled = {
        startDate: new Date(),
        endDate: new Date(),
    };

    const disabledSubmitButton =
        isSubmitting || !values.selectedRequestType || (isLimitExceeded && !requestSummary?.allowOverbooking);
    /*
     * The pool counts against the days actually available: the raw limit knows nothing of the carried
     * over days, the manual corrections or the days added for this person, so it would overstate the
     * excess by exactly those. A request with its own sub-limit keeps counting against that sub-limit -
     * `daysAvailable` describes the pool, which is not what its gate refused.
     */
    const exceededAgainst = requestSummary?.parentWithRestriction
        ? requestSummary?.limit
        : requestSummary?.daysAvailable;
    const exceededNumberOfDays =
        (requestSummary?.daysUsedAdditional ?? 0) + (requestSummary?.daysUsed ?? 0) - (exceededAgainst ?? 0);

    const handleDateRangeChange = useCallback(
        (name, dateRange) => {
            mixpanelTracking(MIXPANEL_ACTIONS.DATE_RANGE_NEW_REQUEST);
            setFieldValue(name, dateRange);
        },
        [setFieldValue]
    );

    const handleCancel = () => {
        if (isScheduleView) {
            onCancel?.();
            return;
        }
        navigate(DEFAULT_PROTECTED_ROUTE);
    };

    return (
        <FormikProvider value={formik}>
            {!!isLead && (
                <FieldWrapper>
                    <UsersSelect
                        name={NEW_REQUEST_FIELD.selectedUser}
                        value={values.selectedUser}
                        onChange={setFieldValue}
                        disabled={isSubmitting || isCreatedSuccess}
                    />
                </FieldWrapper>
            )}

            <FieldWrapper>
                <RequestTypeCards disabled={isSubmitting || isCreatedSuccess} limitsUsage={limitsUsage} />
            </FieldWrapper>

            <Wrapper>
                <DateRangeInput
                    name={NEW_REQUEST_FIELD.dateRange}
                    onChange={handleDateRangeChange}
                    dateRange={values.dateRange}
                    error={isLimitExceeded}
                    disabled={isSubmitting || isCreatedSuccess}
                />

                <Calendar
                    key={isCreatedSuccess ? 'success' : 'initial'}
                    isLimitExceeded={isLimitExceeded}
                    allowOverbooking={requestSummary?.allowOverbooking}
                    exceededNumberOfDays={exceededNumberOfDays}
                    disabled={isSubmitting || isCreatedSuccess}
                    dateRange={values?.dateRange?.endDate !== null ? values.dateRange : endDateNotFilled}
                    onChange={setFieldValue}
                    error={errors?.dateRange as string | undefined}
                    reloadDataOnReset={reloadDataOnReset}
                    touched={Boolean(touched?.dateRange)}
                    userId={values?.selectedUser?.userId}
                />

                <ComponentLoader isLoading={isLoadingSummary}>
                    <Summary requestSummary={requestSummary} />
                </ComponentLoader>

                <FQTextField
                    variant="outlined"
                    data-variant={FQTEXT_FIELD_VARIANTS.fqTextArea}
                    disabled={isSubmitting || isCreatedSuccess}
                    name={NEW_REQUEST_FIELD.comment}
                    value={values?.comment}
                    onChange={handleChange}
                    error={touched?.comment && !!errors?.comment}
                    helperText={touched?.comment && errors?.comment}
                    FormHelperTextProps={{ error: !!errors.comment }}
                    placeholder={t('newApplication_applicationComment_placeholder')}
                    multiline
                    minRows={2}
                    maxRows={2}
                    fullWidth
                    onBlur={handleBlur}
                />

                <ApproversBox userId={values?.selectedUser?.userId} boxTitle={t('profile_common_approvers')} />

                <SuccessModal isOpen={isCreatedSuccess} onReset={onReset} />
                {!isCreatedSuccess && showButtons && (
                    <ButtonsWrapper>
                        <FQButton data-variant={FQ_BUTTON_VARIANTS.fqOutline} color="fqRed" onClick={handleCancel}>
                            {t('alert_defaultDeclineAction_title')}
                        </FQButton>
                        <FQButton
                            disabled={disabledSubmitButton}
                            data-variant={FQ_BUTTON_VARIANTS.fqPrimary}
                            color="fqRed"
                            type="submit"
                            onClick={() => handleSubmit()}
                        >
                            {t(
                                isScheduleView
                                    ? 'newApplication_sendButton_schedule_title'
                                    : 'newApplication_sendButton_title'
                            )}
                        </FQButton>
                    </ButtonsWrapper>
                )}
            </Wrapper>
        </FormikProvider>
    );
};

const FieldWrapper = styled.div`
    margin-bottom: ${PX_TO_REM[20]};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${PX_TO_REM[20]};
`;

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 2.8rem 0;

    button {
        min-width: 9rem;
    }

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.mobile}px) {
        flex-direction: column;
        gap: ${PX_TO_REM[10]};
    }
`;

const Memoized = memo(NewRequestForm);

export default Memoized;
